import app from 'flarum/forum/app';
import humanTime from 'flarum/common/utils/humanTime';
import extractText from 'flarum/common/utils/extractText';
import { registry, fieldRegistry } from '../common/registry';
import HeroBanner from './components/HeroBanner';
const t = (key, params) => params ? app.translator.trans(`ernestdefoe-page-builder.${key}`, params) : app.translator.trans(`ernestdefoe-page-builder.${key}`);
function blockImageUrl(value) {
  if (!value) return null;
  if (typeof value === 'string') return value || null;
  return value.thumbnailUrl || value.url || null;
}

/* Each block is a plain Mithril component receiving { settings, data }. The
 * HTML block trusts pre-sanitized (server-side) markup; everything else renders
 * escaped text or structured data — no m.trust of user input. */

const HeroBlock = {
  view(vnode) {
    return m(HeroBanner, {
      settings: vnode.attrs.settings || {}
    });
  }
};
const HeadingBlock = {
  view(vnode) {
    const s = vnode.attrs.settings || {};
    const tag = ['h1', 'h2', 'h3', 'h4'].includes(s.level) ? s.level : 'h2';
    return m(tag, {
      className: `PB-heading PB-align-${s.align || 'left'}`
    }, s.text || '');
  }
};
const TextBlock = {
  view(vnode) {
    const s = vnode.attrs.settings || {};
    const paras = String(s.content || '').split(/\n+/).map(line => line.trim()).filter(Boolean);
    return m("div", {
      className: `PB-text PB-align-${s.align || 'left'}`
    }, paras.map(p => m("p", null, p)));
  }
};
const HtmlBlock = {
  view(vnode) {
    // Sanitized server-side on save (HtmlSanitizer); safe to trust here.
    return m("div", {
      className: "PB-html"
    }, m.trust(String(vnode.attrs.settings?.html || '')));
  }
};
const ImageBlock = {
  view(vnode) {
    const s = vnode.attrs.settings || {};
    if (!s.url) return null;
    const img = m("img", {
      className: `PB-image ${s.rounded ? 'PB-image--rounded' : ''}`,
      src: s.url,
      alt: s.alt || '',
      loading: "lazy"
    });
    const safeLink = /^https?:\/\//i.test(s.link || '') ? s.link : null;
    return m("div", {
      className: `PB-image-wrap PB-align-${s.align || 'center'}`
    }, safeLink ? m("a", {
      href: safeLink,
      target: "_blank",
      rel: "noopener noreferrer"
    }, img) : img);
  }
};
const ButtonBlock = {
  view(vnode) {
    const s = vnode.attrs.settings || {};
    return m("div", {
      className: `PB-button-wrap PB-align-${s.align || 'left'}`
    }, m("a", {
      className: `Button PB-button PB-button--${s.style || 'primary'}`,
      href: s.url || '#'
    }, s.label || t('forum.block.button_default')));
  }
};
const DividerBlock = {
  view(vnode) {
    const s = vnode.attrs.settings || {};
    return m("div", {
      className: `PB-divider PB-divider--${s.spacing || 'md'}`
    }, s.line !== false && m("hr", null));
  }
};
const RecentDiscussionsBlock = {
  view(vnode) {
    const s = vnode.attrs.settings || {};
    const list = vnode.attrs.data?.discussions || [];
    return m("div", {
      className: "PB-card PB-recent"
    }, s.title && m("h3", {
      className: "PB-card-title"
    }, s.title), list.length === 0 ? m("div", {
      className: "PB-empty"
    }, t('forum.block.recent_empty')) : m("ul", {
      className: "PB-recent-list"
    }, list.map(d =>
    /*
     * 🚨 `--pb-tag` was set on the tag chip alone, so a stylesheet
     * could colour the chip and nothing else — a row could not carry
     * its own forum's colour, which is the one thing that stops a
     * list of discussions reading as one undifferentiated list.
     *
     * The row also names its tag for `data-tag-vars`, which is Tag
     * Covers' declare-only hook: it publishes the tag's crest and
     * cover as custom properties and paints nothing. An earlier
     * version used `data-tag-cover` for this, which is the hook that
     * DRAWS — every board running both extensions would have grown a
     * cover photo behind every row of its discussion list.
     *
     * Neither attribute creates a dependency. Without Tag Covers
     * installed they match no rule and nothing changes.
     */
    m("li", {
      className: "PB-recent-item",
      "data-tag-vars": d.tag ? d.tag.slug : undefined,
      style: d.tag && d.tag.color ? {
        '--pb-tag': d.tag.color
      } : undefined
    }, m("span", {
      className: "PB-recent-avatar"
    }, d.author && d.author.avatar ? m("img", {
      src: d.author.avatar,
      alt: "",
      loading: "lazy"
    }) : m("span", {
      className: "PB-recent-initial"
    }, String(d.author && d.author.name || d.title || '?').charAt(0).toUpperCase())), m("span", {
      className: "PB-recent-body"
    }, m("a", {
      className: "PB-recent-title",
      href: app.route('discussion', {
        id: d.id
      })
    }, d.title), m("span", {
      className: "PB-recent-meta"
    }, d.tag ? m("span", {
      className: "PB-recent-tag",
      style: d.tag.color ? {
        '--pb-tag': d.tag.color
      } : undefined
    }, d.tag.name) : null, d.author ? m("span", {
      className: "PB-recent-who"
    }, d.author.name) : null, d.last_posted_at ? m("span", {
      className: "PB-recent-when"
    }, humanTime(new Date(d.last_posted_at))) : null)), m("span", {
      className: "PB-recent-count"
    }, m("b", null, d.comment_count))))));
  }
};

/* fof/blog articles. `available: false` means the blog extension isn't
 * installed (or has no blog tags configured) — render nothing rather than an
 * empty shell, matching the Tag Grid block's contract. */
const BlogPostsBlock = {
  view(vnode) {
    const s = vnode.attrs.settings || {};
    const data = vnode.attrs.data || {};
    if (data.available === false) return null;
    const posts = data.posts || [];
    return m("div", {
      className: "PB-card PB-blog"
    }, s.title && m("h3", {
      className: "PB-card-title"
    }, s.title), posts.length === 0 ? m("div", {
      className: "PB-empty"
    }, t('forum.block.blog_empty')) : m("ul", {
      className: "PB-blog-list"
    }, posts.map(p => {
      const img = s.show_image !== false ? blockImageUrl(p.featured_image) : null;
      return m("li", {
        className: "PB-blog-item"
      }, m("a", {
        className: "PB-blog-link",
        href: app.route('discussion', {
          id: p.slug || p.id
        })
      }, img && m("img", {
        className: "PB-blog-image",
        src: img,
        alt: "",
        loading: "lazy"
      }), m("span", {
        className: "PB-blog-body"
      }, m("span", {
        className: "PB-blog-title"
      }, p.title), s.show_summary !== false && p.summary && m("span", {
        className: "PB-blog-summary"
      }, p.summary))));
    })));
  }
};
const OnlineUsersBlock = {
  view(vnode) {
    const s = vnode.attrs.settings || {};
    const users = vnode.attrs.data?.users || [];
    return m("div", {
      className: "PB-card PB-online"
    }, s.title && m("h3", {
      className: "PB-card-title"
    }, s.title), users.length === 0 ? m("div", {
      className: "PB-empty"
    }, t('forum.block.online_empty')) : m("div", {
      className: "PB-online-avatars"
    }, users.map(u => m("a", {
      href: app.route('user', {
        username: u.username
      }),
      className: "PB-online-avatar",
      title: u.display_name
    }, u.avatar_url ? m("img", {
      src: u.avatar_url,
      alt: u.display_name
    }) : m("span", {
      className: "PB-online-initials"
    }, (u.display_name || '?').charAt(0))))));
  }
};
const StatsBlock = {
  view(vnode) {
    const s = vnode.attrs.settings || {};
    const d = vnode.attrs.data || {};
    const tiles = [];
    if (s.show_members !== false) tiles.push({
      key: 'members',
      label: extractText(t('forum.block.stat_members')),
      value: d.members ?? 0
    });
    if (s.show_discussions !== false) tiles.push({
      key: 'discussions',
      label: extractText(t('forum.block.stat_discussions')),
      value: d.discussions ?? 0
    });
    if (s.show_posts !== false) tiles.push({
      key: 'posts',
      label: extractText(t('forum.block.stat_posts')),
      value: d.posts ?? 0
    });
    if (s.show_online !== false) tiles.push({
      key: 'online',
      label: extractText(t('forum.block.stat_online')),
      value: d.online ?? 0
    });
    return m("div", {
      className: "PB-card PB-stats"
    }, s.title && m("h3", {
      className: "PB-card-title"
    }, s.title), m("div", {
      className: "PB-stats-grid"
    }, tiles.map(t => m("div", {
      className: "PB-stat"
    }, m("div", {
      className: "PB-stat-value"
    }, formatCount(t.value)), m("div", {
      className: "PB-stat-label"
    }, t.label)))));
  }
};
const RecordListBlock = {
  view(vnode) {
    const s = vnode.attrs.settings || {};
    const data = vnode.attrs.data || {};
    const type = data.type;
    const records = data.records || [];
    if (!type) return null;
    const heading = s.title || type.name_plural;
    const isGrid = (s.layout || 'grid') === 'grid';
    const coverField = (type.fields || []).find(f => f.type === 'image' && f.show_in_list);
    const textFields = (type.fields || []).filter(f => f.show_in_list && f !== coverField);
    return m("div", {
      className: "PB-RecordListBlock"
    }, m("div", {
      className: "PB-RecordListBlock-head"
    }, heading && m("h3", {
      className: "PB-RecordListBlock-title"
    }, heading), s.show_more !== false && m("a", {
      className: "PB-RecordListBlock-more",
      href: `/c/${type.slug}`,
      config: m.route.Link
    }, t('forum.block.view_all'), " ", m("i", {
      className: "fas fa-arrow-right"
    }))), records.length === 0 ? m("div", {
      className: "PB-empty"
    }, t('forum.block.recordlist_empty')) : m("ul", {
      className: `PB-RecordCards ${isGrid && coverField ? 'PB-RecordCards--withCovers' : ''} ${isGrid ? '' : 'PB-RecordCards--compact'}`
    }, records.map(r => {
      const coverUrl = coverField ? blockImageUrl(r.data?.[coverField.key]) : null;
      return m("li", {
        className: "PB-RecordCard"
      }, isGrid && coverField && m("a", {
        className: `PB-RecordCard-cover ${coverUrl ? '' : 'is-empty'}`,
        href: `/c/${type.slug}/${r.slug}`,
        config: m.route.Link,
        style: coverUrl ? {
          backgroundImage: `url(${coverUrl})`
        } : undefined
      }, !coverUrl && m("i", {
        className: type.icon || 'fas fa-file-lines'
      })), m("div", {
        className: "PB-RecordCard-body"
      }, m("a", {
        className: "PB-RecordCard-title",
        href: `/c/${type.slug}/${r.slug}`,
        config: m.route.Link
      }, r.title), m("div", {
        className: "PB-RecordCard-fields"
      }, textFields.slice(0, 1).map(f => {
        const value = r.data?.[f.key];
        if (value === null || value === undefined || value === '') return null;
        const Display = fieldRegistry.display(f.type);
        return Display ? m("div", {
          className: "PB-RecordCard-field"
        }, m(Display, {
          field: f,
          value
        })) : null;
      })), m("div", {
        className: "PB-RecordCard-meta"
      }, r.category && m("span", {
        className: "PB-categoryBadge"
      }, r.category.name), r.author && m("span", null, r.author.display_name), r.published_at && m("span", null, "\xB7 ", new Date(r.published_at).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric'
      })))));
    })));
  }
};
function formatCount(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (v >= 1000) return (v / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return String(v);
}
function shortDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return '';
  }
}
const FeaturedDiscussionsBlock = {
  view(vnode) {
    const s = vnode.attrs.settings || {};
    const items = vnode.attrs.data?.discussions || [];
    return m("div", {
      className: "PB-card PB-featured"
    }, s.title && m("h3", {
      className: "PB-card-title"
    }, s.title), items.length === 0 ? m("div", {
      className: "PB-empty"
    }, t('forum.block.featured_empty')) : m("ul", {
      className: "PB-featured-list"
    }, items.map(d => m("li", {
      className: "PB-featured-item"
    }, m("a", {
      href: app.route('discussion', {
        id: d.id
      }),
      className: "PB-featured-title"
    }, d.title), d.excerpt && m("div", {
      className: "PB-featured-excerpt"
    }, d.excerpt), m("span", {
      className: "PB-featured-meta"
    }, t(d.comment_count === 1 ? 'forum.block.posts_one' : 'forum.block.posts_other', {
      count: d.comment_count
    }))))));
  }
};
const NewestMembersBlock = {
  view(vnode) {
    const s = vnode.attrs.settings || {};
    const users = vnode.attrs.data?.users || [];
    return m("div", {
      className: "PB-card PB-newest"
    }, s.title && m("h3", {
      className: "PB-card-title"
    }, s.title), users.length === 0 ? m("div", {
      className: "PB-empty"
    }, t('forum.block.newest_empty')) : m("ul", {
      className: "PB-newest-list"
    }, users.map(u => m("li", {
      className: "PB-newest-item"
    }, m("a", {
      href: app.route('user', {
        username: u.username
      }),
      className: "PB-newest-user"
    }, u.avatar_url ? m("img", {
      src: u.avatar_url,
      alt: u.display_name
    }) : m("span", {
      className: "PB-newest-initials"
    }, (u.display_name || '?').charAt(0)), m("span", {
      className: "PB-newest-name"
    }, u.display_name)), s.show_date !== false && u.joined_at && m("span", {
      className: "PB-newest-date"
    }, shortDate(u.joined_at))))));
  }
};
const TagGridBlock = {
  view(vnode) {
    const s = vnode.attrs.settings || {};
    const data = vnode.attrs.data || {};
    if (data.available === false) return null; // flarum/tags not installed
    const tags = data.tags || [];
    return m("div", {
      className: "PB-card PB-tagGrid"
    }, s.title && m("h3", {
      className: "PB-card-title"
    }, s.title), tags.length === 0 ? m("div", {
      className: "PB-empty"
    }, t('forum.block.taggrid_empty')) : m("div", {
      className: "PB-tagGrid-grid"
    }, tags.map(tag => m("a", {
      className: "PB-tagCard",
      href: `/t/${tag.slug}`
      /* The colour as a variable as well as a border, so a card can
         do more with it than draw one line. */,
      style: tag.color ? {
        borderTopColor: tag.color,
        '--pb-tag': tag.color
      } : undefined
    }, m("span", {
      className: "PB-tagCard-name"
    }, tag.icon && m("i", {
      className: tag.icon
    }), " ", tag.name), s.show_count !== false && m("span", {
      className: "PB-tagCard-count"
    }, t('forum.block.discussions', {
      count: formatCount(tag.discussion_count)
    }))))));
  }
};

/** Turn a YouTube/Vimeo URL into an embeddable player src, or null. */
function embedSrc(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
}
const EmbedBlock = {
  view(vnode) {
    const s = vnode.attrs.settings || {};
    const url = String(s.url || '');
    const src = embedSrc(url);
    const ratio = ['16x9', '4x3', '1x1'].includes(s.ratio) ? s.ratio : '16x9';
    return m("div", {
      className: "PB-embed"
    }, src ? m("div", {
      className: `PB-embed-frame PB-embed-frame--${ratio}`
    }, m("iframe", {
      src: src,
      title: s.caption || extractText(t('forum.block.embed_title')),
      allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
      allowfullscreen: true
    })) : url ? m("a", {
      className: "PB-embed-fallback",
      href: url,
      target: "_blank",
      rel: "noopener noreferrer"
    }, m("i", {
      className: "fas fa-external-link-alt"
    }), " ", url) : m("div", {
      className: "PB-empty"
    }, t('forum.block.embed_prompt')), s.caption && m("div", {
      className: "PB-embed-caption"
    }, s.caption));
  }
};

/** Register every core block's component under its type key. */
export function registerCoreBlocks() {
  registry.registerBlock('hero', HeroBlock);
  registry.registerBlock('heading', HeadingBlock);
  registry.registerBlock('text', TextBlock);
  registry.registerBlock('html', HtmlBlock);
  registry.registerBlock('image', ImageBlock);
  registry.registerBlock('button', ButtonBlock);
  registry.registerBlock('divider', DividerBlock);
  registry.registerBlock('recent-discussions', RecentDiscussionsBlock);
  registry.registerBlock('featured-discussions', FeaturedDiscussionsBlock);
  registry.registerBlock('online-users', OnlineUsersBlock);
  registry.registerBlock('newest-members', NewestMembersBlock);
  registry.registerBlock('stats', StatsBlock);
  registry.registerBlock('record-list', RecordListBlock);
  registry.registerBlock('tag-grid', TagGridBlock);
  registry.registerBlock('blog-posts', BlogPostsBlock);
  registry.registerBlock('embed', EmbedBlock);
}